import React, { useState, useEffect, useRef } from "react";

interface SourceImage {
  blob: Blob;
  url: string;
  name: string;
  width?: number;
  height?: number;
}

interface AnimationConfig {
  frameDelay: number; // in ms internally
  crossfade: boolean;
}

interface AnimationConfigModalProps {
  images: SourceImage[];
  frameDelay: number;
  crossfade: boolean;
  onSave: (config: AnimationConfig) => void;
  onCancel: () => void;
}

const AnimationConfigModal: React.FC<AnimationConfigModalProps> = ({
  images,
  frameDelay,
  crossfade,
  onSave,
  onCancel,
}) => {
  // Convert ms to seconds for display
  const [localDelaySeconds, setLocalDelaySeconds] = useState(frameDelay / 1000);
  const [localCrossfade, setLocalCrossfade] = useState(crossfade);
  const [currentFrame, setCurrentFrame] = useState(0);
  const [nextFrame, setNextFrame] = useState(1);
  const [blendProgress, setBlendProgress] = useState(0); // 0 to 1
  const [isPlaying, setIsPlaying] = useState(true);
  const animationRef = useRef<number | null>(null);
  const lastTimeRef = useRef<number>(0);

  // Animate through frames with crossfade support
  useEffect(() => {
    if (!isPlaying || images.length < 2) return;

    const delayMs = localDelaySeconds * 1000;
    // For crossfade: transition takes 1/3 of delay, hold takes 2/3
    const transitionDuration = localCrossfade ? delayMs / 3 : 0;
    const holdDuration = localCrossfade ? (delayMs * 2) / 3 : delayMs;

    let frameTime = 0;
    let isTransitioning = false;

    const animate = (timestamp: number) => {
      if (!lastTimeRef.current) {
        lastTimeRef.current = timestamp;
      }

      const deltaTime = timestamp - lastTimeRef.current;
      lastTimeRef.current = timestamp;
      frameTime += deltaTime;

      if (localCrossfade) {
        if (!isTransitioning) {
          // Holding on current frame
          if (frameTime >= holdDuration) {
            isTransitioning = true;
            frameTime = 0;
          }
          setBlendProgress(0);
        } else {
          // Transitioning to next frame
          const progress = Math.min(1, frameTime / transitionDuration);
          setBlendProgress(progress);

          if (frameTime >= transitionDuration) {
            // Transition complete
            setCurrentFrame((prev) => (prev + 1) % images.length);
            setNextFrame((prev) => (prev + 1) % images.length);
            isTransitioning = false;
            frameTime = 0;
            setBlendProgress(0);
          }
        }
      } else {
        // No crossfade - simple frame switch
        if (frameTime >= delayMs) {
          setCurrentFrame((prev) => (prev + 1) % images.length);
          setNextFrame((prev) => (prev + 1) % images.length);
          frameTime = 0;
        }
        setBlendProgress(0);
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    lastTimeRef.current = 0;
    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isPlaying, localDelaySeconds, localCrossfade, images.length]);

  // Reset frames when crossfade changes
  useEffect(() => {
    setCurrentFrame(0);
    setNextFrame(1);
    setBlendProgress(0);
  }, [localCrossfade]);

  const handleSave = () => {
    // Convert seconds back to ms
    const delayMs = Math.round(localDelaySeconds * 1000);
    onSave({ frameDelay: delayMs, crossfade: localCrossfade });
  };

  // Format display value
  const formatDelay = (seconds: number) => {
    return `${seconds.toFixed(2)}s`;
  };

  return (
    <div className="grid-config-overlay" onClick={onCancel}>
      <div className="grid-config-modal" onClick={(e) => e.stopPropagation()}>
        <div className="grid-config-header">
          <h3>Animation Settings</h3>
          <button className="grid-config-close-btn" onClick={onCancel}>
            &times;
          </button>
        </div>

        <div className="grid-config-content">
          {/* Preview */}
          <div className="grid-config-section">
            <label className="grid-config-label">Preview</label>
            <div className="grid-preview-container">
              {images.length >= 2 ? (
                <div className="animation-preview-stack">
                  {/* Current frame (bottom) */}
                  <img
                    src={images[currentFrame]?.url}
                    alt={`Frame ${currentFrame + 1}`}
                    className="animation-preview-frame"
                    style={{ opacity: 1 - blendProgress }}
                  />
                  {/* Next frame (top, fading in during crossfade) */}
                  {localCrossfade && blendProgress > 0 && (
                    <img
                      src={images[nextFrame]?.url}
                      alt={`Frame ${nextFrame + 1}`}
                      className="animation-preview-frame animation-preview-frame-top"
                      style={{ opacity: blendProgress }}
                    />
                  )}
                </div>
              ) : (
                <div className="grid-preview-placeholder">
                  Add at least 2 images
                </div>
              )}
            </div>
            <span className="grid-preview-info">
              Frame {currentFrame + 1} of {images.length}
              <button
                type="button"
                className="animation-play-btn"
                onClick={() => setIsPlaying(!isPlaying)}
                style={{ marginLeft: "8px" }}
              >
                {isPlaying ? "Pause" : "Play"}
              </button>
            </span>
          </div>

          {/* Delay slider */}
          <div className="grid-config-section">
            <label className="grid-config-label">Frame Delay</label>
            <div className="grid-config-slider-row">
              <input
                type="range"
                min="0.01"
                max="2"
                step="0.01"
                value={localDelaySeconds}
                onChange={(e) => setLocalDelaySeconds(parseFloat(e.target.value))}
                className="grid-config-slider"
              />
              <span className="grid-config-value">{formatDelay(localDelaySeconds)}</span>
            </div>
          </div>

          {/* Crossfade option */}
          <div className="grid-config-section">
            <label className="animation-crossfade-toggle">
              <input
                type="checkbox"
                checked={localCrossfade}
                onChange={(e) => setLocalCrossfade(e.target.checked)}
              />
              <span className="checkmark" />
              <span className="crossfade-label">
                Crossfade
                <span className="crossfade-description">Blend between frames</span>
              </span>
            </label>
          </div>
        </div>

        <div className="grid-config-actions">
          <button className="grid-config-cancel-btn" onClick={onCancel}>
            Cancel
          </button>
          <button
            className="grid-config-save-btn"
            onClick={handleSave}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnimationConfigModal;
export type { AnimationConfig };
